import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AgGridWrapperTracker from '@/app/common/components/ag-grid/AgGridWrapperTracker';
import { useRef, useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import type { AgGridReact } from 'ag-grid-react';
import {
  useLocation,
  useOutletContext,
  useSearchParams
} from 'react-router-dom';
import type { OutletContext } from './pageTypes';
import type { GridRef } from '../components/keywords/drawers/types';import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import keywordsApi, {
  useDeleteLandingUrlsMutation,
  useEditLandingMutation,
  useGetLandingUrlsFuncMutation,
  useGetLandingUrlsQuery,
  useGetTariffInfoQuery,
} from '@/app/KeywordTracker/api/keywordsApi';
import { deleteTransaction } from '@/app/common/components/ag-grid/deleteTransaction';
import AddLandingUrlsDrawer from '../components/keywords/drawers/AddLandingUrlsDrawer';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { landingUrlsColumnConfig } from '@/app/KeywordTracker/config/landingUrlsColumnConfig';
import { useSelector } from 'react-redux';
import CancelModal from '@/app/common/components/CancelModal';
import { errorMessage, successMessage } from '@/utils/toast';
import Flex from '@/components/common/Flex';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import TimePicker from '../components/common/TimePicker';import { useDispatch } from 'react-redux';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { AiOutlineFileText } from 'react-icons/ai';import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import { hideModal, showModal } from '@/app/common/reducers/simpleModal';
import {
  isTrackerKeywordsTariffZero,
  TRACKER_KEYWORDS_INACTIVE_TOOLTIP
} from '@/app/KeywordTracker/utils/trackerTariff';
import TrackerKeywordsInactiveAlert from '../components/TrackerKeywordsInactiveAlert';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

const LandingUrls = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const gridRef = useRef<AgGridReact | null>(null);
  const { domain_slug, domain } = useDomain();
  const { data: userInfo } = useGetUserInfoQuery();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [editLanding] = useEditLandingMutation();
  useEffect(() => {
    setBreadcrumbData([{ text: 'صفحه هدف', href: '#' }]);
  }, [setBreadcrumbData]);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    dispatch(keywordsApi.util.invalidateTags(['landing_urls']));
  }, [dispatch, pathname, domain, domain_slug]);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetLandingUrlsQuery({
      domain_slug,
      from_date: Number(searchParams.get('from_date')),
      to_date: Number(searchParams.get('to_date'))
    });
  const [getLandingFun] = useGetLandingUrlsFuncMutation();
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isTrackerAddonInactive = () =>
    isTrackerKeywordsTariffZero(trackerInfo);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  const getAddButtonTooltip = () => {
    if (isTrackerAddonInactive()) {
      return TRACKER_KEYWORDS_INACTIVE_TOOLTIP;
    }
    if (!isAccessAllowed()) {
      return 'شما دسترسی لازم را ندارید';
    }
    return '';
  };

  return (
    <>
      {!isFetchingTrackerInfo && isTrackerAddonInactive() && (
        <div className="my-3">
          <TrackerKeywordsInactiveAlert />
        </div>
      )}
      <Row className="g-3 g-md-0 my-3 p-0 justify-content-between">
        <Col xs={12} sm="auto">
          <Flex alignItems="center" className="gap-2 w-100">
            <ChildrensTooltip
              className="w-100"
              placement="top"
              html={getAddButtonTooltip()}
            >
              <Button
                disabled={isTrackerAddonInactive() || !isAccessAllowed()}
                onClick={() => dispatch(showModal('ADD_LANDING'))}
                variant="falcon-primary"
                size="sm"
                className="d-flex w-100 py-1 gap-2 justify-content-center align-items-center"
              >
                {isTrackerAddonInactive() ? (
                  <FontAwesomeIcon icon="lock" className="fs--1" />
                ) : (
                  <AiOutlineFileText className="fs-1" />
                )}{' '}
                افزودن صفحه هدف
              </Button>
            </ChildrensTooltip>
          </Flex>
        </Col>

        <Col xs={12} sm="auto" className="mb-3 mb-md-0">
          <Row className="g-2 gy-md-0 h-100 align-items-center">
            <Col xs="auto">
              <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
            </Col>
            <Col xs={12} md="auto">
              {!isFetching && !isLoading ? (
                <TimePicker
                  initDay={data?.data?.from_date}
                  lastDay={data?.data?.to_date}
                />
              ) : (
                <TextLoading
                  width={isMdUp ? 205 : 100}
                  height={24}
                  isWidthPx={isMdUp}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <div>
        {shouldShowDataLoadError(isError, error) ? (
          <DataLoadError
            error={error}
            onRetry={() => refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        ) : !isFetching && !isLoading ? (
          <>
            {/* FOR BUILD NOW */}
            <FiltersComponent
              columns={landingUrlsColumnConfig(
                domain_slug,
                domain,
                editLanding,
                userInfo?.data?.path,
                gridRef
              )}
              preQueries={
                searchParams.get('from_date') || searchParams.get('to_date')
                  ? `from_date=${searchParams.get('from_date')}&to_date=${searchParams.get('to_date')}`
                  : ''
              }
            />
            <AgGridWrapperTracker
              ref={gridRef}
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: true,
                //   resetGrid: true,
                saveGrid: false,
                sideBar: true,
                // columnsState: 'tableSettings?.data',
                export: {
                  enabled: false
                  // fetchFunction: 'exportTable'
                }
              }}
              perPage={20}
              otherButtons={
                <div className="d-flex gap-1 align-items-center">
                  <GridButtons isAccessAllowed={isAccessAllowed} />
                </div>
              }
              height="746px"
              data={data?.data?.rows}
              total={data?.data?.total}
              fetchFunction={getLandingFun}
              additionalData={{
                domain_slug,
                from_date: Number(searchParams.get('from_date')),
                to_date: Number(searchParams.get('to_date'))
              }}
              columns={landingUrlsColumnConfig(
                domain_slug,
                domain,
                editLanding,
                userInfo?.data?.path,
                gridRef
              )}
            />
            <AddLandingUrlsDrawer gridRef={gridRef} />
            <DeleteModalWrapper gridRef={gridRef} />
          </>
        ) : (
          <div>
            {/* FOR BUILD NOW */}

            <Col xxl={12} className="mb-2">
              <FiltersComponentSkeleton />
            </Col>
            <AgGridSkeleton
              height="696px"
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: true,
                resetGrid: false,
                saveGrid: false,
                exportBtn: false
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default LandingUrls;

interface DeleteModalWrapperProps {
  gridRef: GridRef;
}

const DeleteModalWrapper = ({ gridRef }: DeleteModalWrapperProps) => {  const selectedKeys = useSelector(state => state.selectedKeys);
  const { domain_slug } = useDomain();
  const [deleteLanding] = useDeleteLandingUrlsMutation();
  const modalKey = useSelector(state => state.simpleModal);
  const dispatch = useDispatch();
  return (
    <CancelModal
      desc={
        <Form.Group>
          <Form.Label>
            آیا از حذف {selectedKeys.length} صفحه هدف زیر مطمئن هستی؟
          </Form.Label>
          <Form.Control
            // readOnly
            disabled
            rows={selectedKeys.length > 3 ? 3 : selectedKeys.length}
            as="textarea"
            dir="ltr"
            value={selectedKeys.map(({ label }) => `${label}`).join('\n')}
          />
          <div className="d-flex mt-2 align-items-center gap-2">
            <FontAwesomeIcon
              icon="exclamation-circle"
              className="text-warning"
            />
            پس از حذف صفحه هدف کلمات دیگر این صفحه هدف رو نخواهند داشت.
          </div>
        </Form.Group>
      }
      show={modalKey === 'DELETE_LANDING'}
      setShow={() => dispatch(hideModal())}
      title="حذف صفحه هدف"
      submitFunc={() => {
        deleteLanding({
          domain_slug,
          landing_url_ids: selectedKeys.map(key => key.id)
        })
          .unwrap()
          .then(res => {
            deleteTransaction(res?.data, gridRef);

            successMessage('حذف صفحه هدف با موفقیت انجام شد.');
          })
          .catch(err => {
            errorMessage('خطایی در حذف صفحه هدف وجود داره');
            console.error(err);
          })
          .finally(() => {
            dispatch(hideModal());
          });
      }}
      submitTitle="آره، حذف بشه"
    />
  );
};

interface GridButtonsProps {
  isAccessAllowed: () => boolean;
}

const GridButtons = ({ isAccessAllowed }: GridButtonsProps) => {  const modalKey = useSelector(state => state.simpleModal);
  const dispatch = useDispatch();

  const selectedKeys = useSelector(state => state.selectedKeys);

  return (
    selectedKeys.length > 0 && (
      <ChildrensTooltip
        html={isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'}
      >
        <Button
          style={{
            padding: 12,
            height: 30
          }}
          disabled={!isAccessAllowed()}
          onClick={() => dispatch(showModal('DELETE_LANDING'))}
          variant="danger"
          size="sm"
          className="d-flex py-2 gap-2 align-items-center"
        >
          <FontAwesomeIcon icon="trash-alt" className="fs-0" />{' '}
        </Button>
      </ChildrensTooltip>
    )
  );
};
